import { CheckCircle, Bot, User, X, XCircle } from "lucide-react";
import { type FC, type JSX, useEffect, useMemo, useState } from "react";
import { Appointment, 
    // AppointmentCalender 
} from "../../services/api/appointment/appointmentApi";
import { formatDate, formatTimeString } from "../../lib/utils/date-time";
import Button from "../shared/Button";
import DatePicker from "../shared/DatePicker";
import TimePicker from "../shared/TimePicker";
import { useCancelAppointment, useRescheduleAppointment } from "../../services/query/appointment/appointment";
import { toast } from "../../lib/utils/toast";
import { getErrorMessage, toAppointmentDatetimeISO } from "../../lib/utils/helpers";
import { TIME_SLOTS } from "../../enums";



interface AppointmentDetailsProps {
    selectedAppointment: Appointment;
    statusColor:string;
    appointment_date?: string;
    setSelectedAppointment: (appointment: React.SetStateAction<Appointment | null>) => void;
}





const AppointmentDetails: FC<AppointmentDetailsProps> = ({ 
    selectedAppointment,
    appointment_date,
    statusColor,
    setSelectedAppointment,
}): JSX.Element => {
    const { mutate: rescheduleAppointment, isPending: isRescheduling } = useRescheduleAppointment();
    const { mutate: cancelAppointment, isPending: isCanceling } = useCancelAppointment();

    const initialRescheduleDate = useMemo(() => {
        return appointment_date || new Date().toISOString().split('T')[0];
    }, [appointment_date]);

    const initialRescheduleTime = useMemo(() => {
        const match = selectedAppointment.start_time?.match(/(\d{1,2}):(\d{2})/);
        const hhmm = match ? `${match[1].padStart(2, '0')}:${match[2]}` : '09:00';
        return (TIME_SLOTS as readonly string[]).includes(hhmm) ? hhmm : '09:00';
    }, [selectedAppointment.start_time]);

    const [rescheduleDate, setRescheduleDate] = useState<string>(initialRescheduleDate);
    const [rescheduleTime, setRescheduleTime] = useState<string>(initialRescheduleTime);

    useEffect(() => {
        setRescheduleDate(initialRescheduleDate);
        setRescheduleTime(initialRescheduleTime);
    }, [initialRescheduleDate, initialRescheduleTime]);

    const appointmentDatetimeISO = useMemo(
        () => toAppointmentDatetimeISO(rescheduleDate, rescheduleTime),
        [rescheduleDate, rescheduleTime]
    );

    const handleRescheduleConfirmed = () => {
        rescheduleAppointment(
            {
                appointmentId: selectedAppointment.id,
                appointment_datetime: appointmentDatetimeISO,
            },
            {
                onSuccess: () => {
                    toast.success('Appointment rescheduled successfully');
                    setSelectedAppointment(null);
                },
                onError: (error: unknown) => {
                    toast.error(getErrorMessage(error, 'Failed to reschedule appointment'));
                },
            }
        );
    };

    const handleCancelAppointment = () => {
        cancelAppointment(
            { appointmentId: selectedAppointment.id },
            {
                onSuccess: () => {
                    toast.success('Appointment canceled successfully');
                    setSelectedAppointment(null);
                },
                onError: (error: unknown) => {
                    toast.error(getErrorMessage(error, 'Failed to cancel appointment'));
                },
            }
        );
    };

    return (
        <>
            <div className="fixed inset-0 top-[-30px] bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg w-full max-w-xl">
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">Appointment Details</h3>
                        <p className="text-sm text-gray-600">{selectedAppointment.user_name}</p>
                    </div>
                    <button
                        onClick={() => setSelectedAppointment(null)}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                    >
                        <X className="w-6 h-6" />
                    </button>
                    </div>

                    <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date & Time</label>
                        <p className="text-gray-900 font-semibold">
                            {formatDate(String(appointment_date))} at {formatTimeString(selectedAppointment.start_time)}
                        </p>
                        </div>

                        <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Type & Duration</label>
                        <p className="text-gray-900 font-semibold capitalize">
                            {'Consultation'} ({selectedAppointment.duration_minutes} min)
                        </p>
                        </div>

                        <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <span className={`inline-block px-3 py-1 ${statusColor} rounded-full text-sm font-semibold capitalize`}>
                            {selectedAppointment.status}
                        </span>
                        </div>

                        <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Booking Method</label>
                        <div className="flex items-center space-x-2">  
                                {
                                    selectedAppointment.created_by.toLowerCase()
                                    .includes('agent') ? (
                                        <>
                                            <Bot className="w-4 h-4 text-purple-600" />
                                        
                                            <span className="text-gray-900 font-semibold">Agent Booked</span>
                                        </>
                                    ) : (
                                        <>
                                            <User className="w-4 h-4 text-blue-600" />
                                            <span className="text-gray-900 font-semibold">Staff Booked</span>
                                        </>
                                    )
                                }
                        </div>
                        </div>
                    </div>

                    {/* Notes */}
                    {selectedAppointment.reason && (
                        <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                        <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{selectedAppointment.reason}</p>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="border-t border-gray-200 pt-4">

                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <DatePicker
                                label="Reschedule Date"
                                hint="Select a new date for the appointment"
                                value={rescheduleDate}
                                onChange={setRescheduleDate}
                                min={new Date().toISOString().split('T')[0]}
                                disabled={isRescheduling || isCanceling}
                            />

                            <TimePicker
                                label="Reschedule Time"
                                hint="Select a new time for the appointment"
                                value={rescheduleTime}
                                onChange={setRescheduleTime}
                                options={TIME_SLOTS}
                                disabled={isRescheduling || isCanceling}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                        <Button
                            rightIcon={<CheckCircle className="w-5 h-5" />}
                            onClick={handleRescheduleConfirmed}
                            loading={isRescheduling}
                            disabled={isRescheduling || isCanceling}
                            className="flex bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                            <span>Reschedule Confirmed</span>
                        </Button>

                        <Button
                            rightIcon={<XCircle className="w-5 h-5" />}
                            onClick={handleCancelAppointment}
                            loading={isCanceling}
                            disabled={isRescheduling || isCanceling}
                            className="flex bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                            <span>Cancel Appointment</span>
                        </Button>

                        {/* <Button
                            rightIcon={<CheckCircle className="w-5 h-5" />}
                            onClick={() => updateAppointmentStatus(selectedAppointment, 'completed')}
                            className="flex bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <span>Mark Completed</span>
                        </Button> */}
                        </div>
                    </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default AppointmentDetails;